import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Tabs,
  Form,
  Input,
  Button,
  Typography,
  Checkbox,
  Row,
  Col,
} from "antd";
import { Mail, Lock, User, Building, MapPin } from "lucide-react";
import Particles from "../Particels";
import { useAuthService } from "../../services/authService";
import { useAuth } from "../../context/authContext";
import { useLanguage } from "../../context/LanguageContext";

const { Title } = Typography;
const { TabPane } = Tabs;

export const AuthPage = () => {
  const [activeTab, setActiveTab] = useState("login");
  const { loginUser, register } = useAuthService();
  const [loading, setLoading] = useState(false);
  const [isCompany, setIsCompany] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { language } = useLanguage();

  // Localization object
  const translations = {
    de: {
      login: "Anmelden",
      register: "Registrieren",
      welcomeBack: "Willkommen zurück",
      createAccount: "Konto erstellen",
      fullName: "Vollständiger Name",
      email: "E-Mail",
      password: "Passwort",
      confirmPassword: "Passwort bestätigen",
      loginButton: "Anmelden",
      registerButton: "Registrieren",
      forgotPassword: "Passwort vergessen?",
      resetPassword: "Passwort zurücksetzen",
      // Company fields
      isCompany: "Ich registriere mich als Unternehmen",
      companyName: "Firmenname",
      street: "Straße und Hausnummer",
      postalCode: "Postleitzahl",
      city: "Stadt",
      country: "Land",
      // Validation messages
      nameRequired: "Bitte geben Sie Ihren Namen ein!",
      emailRequired: "Bitte geben Sie Ihre E-Mail ein!",
      emailInvalid: "Bitte geben Sie eine gültige E-Mail ein!",
      passwordRequired: "Bitte geben Sie Ihr Passwort ein!",
      passwordMinLength: "Das Passwort muss mindestens 8 Zeichen lang sein!",
      confirmPasswordRequired: "Bitte bestätigen Sie Ihr Passwort!",
      passwordsMismatch: "Die beiden Passwörter stimmen nicht überein!",
      companyNameRequired: "Bitte geben Sie den Firmennamen ein!",
      streetRequired: "Bitte geben Sie die Straße ein!",
      postalCodeRequired: "Bitte geben Sie die Postleitzahl ein!",
      cityRequired: "Bitte geben Sie die Stadt ein!",
      countryRequired: "Bitte geben Sie das Land ein!",
    },
    en: {
      login: "Login",
      register: "Register",
      welcomeBack: "Welcome back",
      createAccount: "Create an account",
      fullName: "Full Name",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm Password",
      loginButton: "Log in",
      registerButton: "Register",
      forgotPassword: "Forgot password?",
      resetPassword: "Reset password",
      // Company fields
      isCompany: "I'm registering as a company",
      companyName: "Company Name",
      street: "Street and Number",
      postalCode: "Postal Code",
      city: "City",
      country: "Country",
      // Validation messages
      nameRequired: "Please input your name!",
      emailRequired: "Please input your email!",
      emailInvalid: "Please enter a valid email!",
      passwordRequired: "Please input your password!",
      passwordMinLength: "Password must be at least 8 characters long!",
      confirmPasswordRequired: "Please confirm your password!",
      passwordsMismatch: "The two passwords do not match!",
      companyNameRequired: "Please input the company name!",
      streetRequired: "Please input the street!",
      postalCodeRequired: "Please input the postal code!",
      cityRequired: "Please input the city!",
      countryRequired: "Please input the country!",
    },
  };

  const t = translations[language] || translations.en;

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      if (activeTab === "login") {
        await loginUser(values.email, values.password);
      } else {
        // Erweiterte Registrierungsdaten
        const registrationData = {
          name: values.name,
          email: values.email,
          password: values.password,
          isCompany: isCompany,
          ...(isCompany && {
            companyName: values.companyName,
            address: {
              street: values.street,
              postalCode: values.postalCode,
              city: values.city,
              country: values.country,
            },
          }),
        };

        await register(registrationData);
        setActiveTab("login");
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-10 bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 overflow-hidden relative">
      <Particles />
      <img src="./rdy.png" alt="Logo Reebaq" />
      <Card className="w-full max-w-md z-10 bg-white shadow-lg">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          className="w-full custom-tabs"
        >
          <TabPane tab={t.login} key="login" />
          <TabPane tab={t.register} key="register" />
        </Tabs>

        <Form
          name={activeTab}
          onFinish={handleSubmit}
          layout="vertical"
          className="space-y-4"
        >
          <Title level={3} className="text-center mb-6">
            {activeTab === "login" ? t.welcomeBack : t.createAccount}
          </Title>

          {activeTab === "register" && (
            <>
              {/* Checkbox für Firmenregistrierung */}
              <Form.Item>
                <Checkbox
                  checked={isCompany}
                  onChange={(e) => setIsCompany(e.target.checked)}
                  className="mb-4"
                >
                  {t.isCompany}
                </Checkbox>
              </Form.Item>

              {/* Name/Ansprechpartner */}
              <Form.Item
                name="name"
                rules={[{ required: true, message: t.nameRequired }]}
              >
                <Input
                  prefix={<User className="text-gray-400" size={18} />}
                  placeholder={
                    isCompany
                      ? language === "de"
                        ? "Ansprechpartner"
                        : "Contact Person"
                      : t.fullName
                  }
                  size="large"
                />
              </Form.Item>

              {/* Firmenname - nur wenn Firma */}
              {isCompany && (
                <Form.Item
                  name="companyName"
                  rules={[{ required: true, message: t.companyNameRequired }]}
                >
                  <Input
                    prefix={<Building className="text-gray-400" size={18} />}
                    placeholder={t.companyName}
                    size="large"
                  />
                </Form.Item>
              )}

              {/* Adressfelder - nur wenn Firma */}
              {isCompany && (
                <>
                  <Form.Item
                    name="street"
                    rules={[{ required: true, message: t.streetRequired }]}
                  >
                    <Input
                      prefix={<MapPin className="text-gray-400" size={18} />}
                      placeholder={t.street}
                      size="large"
                    />
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item
                        name="postalCode"
                        rules={[
                          { required: true, message: t.postalCodeRequired },
                        ]}
                      >
                        <Input placeholder={t.postalCode} size="large" />
                      </Form.Item>
                    </Col>
                    <Col span={16}>
                      <Form.Item
                        name="city"
                        rules={[{ required: true, message: t.cityRequired }]}
                      >
                        <Input placeholder={t.city} size="large" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="country"
                    rules={[{ required: true, message: t.countryRequired }]}
                  >
                    <Input
                      placeholder={t.country}
                      size="large"
                      defaultValue="Deutschland"
                    />
                  </Form.Item>
                </>
              )}
            </>
          )}

          <Form.Item
            name="email"
            rules={[
              { required: true, message: t.emailRequired },
              { type: "email", message: t.emailInvalid },
            ]}
          >
            <Input
              prefix={<Mail className="text-gray-400" size={18} />}
              placeholder={t.email}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: t.passwordRequired },
              {
                min: 8,
                message: t.passwordMinLength,
              },
            ]}
          >
            <Input.Password
              prefix={<Lock className="text-gray-400" size={18} />}
              placeholder={t.password}
              size="large"
            />
          </Form.Item>

          {activeTab === "register" && (
            <Form.Item
              name="confirmPassword"
              dependencies={["password"]}
              rules={[
                { required: true, message: t.confirmPasswordRequired },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t.passwordsMismatch));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<Lock className="text-gray-400" size={18} />}
                placeholder={t.confirmPassword}
                size="large"
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              className="w-full bg-[#FF7051] hover:!bg-[#FF7051]/90"
            >
              {activeTab === "login" ? t.loginButton : t.registerButton}
            </Button>
          </Form.Item>

          {activeTab === "login" && (
            <h1 onClick={() => navigate("/forget-password")}>
              {t.forgotPassword}{" "}
              <span className="text-orange-500 cursor-pointer">
                {t.resetPassword}
              </span>
            </h1>
          )}
        </Form>
      </Card>
    </div>
  );
};
